// MoodSelector - Multi-Select Stimmungsauswahl mit Kategorien

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { EXTENDED_MOOD_OPTIONS, MOOD_CATEGORIES, getMoodByValue } from '../../constants/moods';
import type { LogbookMoodCategory } from '../../types';

interface MoodSelectorProps {
  selectedMoods: string[];
  onChange: (moods: string[]) => void;
  maxMoods?: number;
  compact?: boolean;
}

export function MoodSelector({
  selectedMoods,
  onChange,
  maxMoods = 5,
  compact = false,
}: MoodSelectorProps) {
  const [expandedCategory, setExpandedCategory] = useState<LogbookMoodCategory | null>(
    compact ? null : 'positive'
  );

  const toggleMood = (value: string) => {
    if (selectedMoods.includes(value)) {
      onChange(selectedMoods.filter((m) => m !== value));
    } else if (selectedMoods.length < maxMoods) {
      onChange([...selectedMoods, value]);
    }
  };

  const removeMood = (value: string) => {
    onChange(selectedMoods.filter((m) => m !== value));
  };

  const toggleCategory = (category: LogbookMoodCategory) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const getMoodsForCategory = (category: LogbookMoodCategory) => {
    return EXTENDED_MOOD_OPTIONS.filter((m) => m.category === category);
  };

  return (
    <div className="space-y-3">
      {/* Ausgewählte Moods als Chips */}
      {selectedMoods.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedMoods.map((value) => {
            const mood = getMoodByValue(value);
            if (!mood) return null;
            return (
              <span
                key={value}
                className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm"
              >
                <span>{mood.emoji}</span>
                <span>{mood.label}</span>
                <button
                  type="button"
                  onClick={() => removeMood(value)}
                  className="ml-1 hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Hinweis wenn Maximum erreicht */}
      {selectedMoods.length >= maxMoods && (
        <p className="text-xs text-amber-600">
          Maximum von {maxMoods} Stimmungen erreicht
        </p>
      )}

      {/* Kategorien */}
      <div className="space-y-2">
        {MOOD_CATEGORIES.map((category) => (
          <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Kategorie-Header */}
            <button
              type="button"
              onClick={() => toggleCategory(category.id)}
              className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <span>{category.emoji}</span>
                <span>{category.label}</span>
                {/* Anzahl ausgewählter Moods in dieser Kategorie */}
                {selectedMoods.filter((m) => getMoodByValue(m)?.category === category.id).length > 0 && (
                  <span className="bg-indigo-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {selectedMoods.filter((m) => getMoodByValue(m)?.category === category.id).length}
                  </span>
                )}
              </span>
              {expandedCategory === category.id ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>

            {/* Mood-Optionen */}
            {expandedCategory === category.id && (
              <div className="p-2 flex flex-wrap gap-2">
                {getMoodsForCategory(category.id).map((mood) => {
                  const isSelected = selectedMoods.includes(mood.value);
                  const isDisabled = !isSelected && selectedMoods.length >= maxMoods;

                  return (
                    <button
                      key={mood.value}
                      type="button"
                      onClick={() => toggleMood(mood.value)}
                      disabled={isDisabled}
                      className={`
                        px-3 py-1.5 rounded-full text-sm transition-all
                        ${isSelected
                          ? 'bg-indigo-500 text-white shadow-sm'
                          : isDisabled
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white border border-gray-300 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50'
                        }
                      `}
                    >
                      <span className="mr-1">{mood.emoji}</span>
                      <span>{mood.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Hilfetext */}
      {selectedMoods.length === 0 && (
        <p className="text-xs text-gray-500">
          Wähle bis zu {maxMoods} Stimmungen, die deinen Besuch beschreiben
        </p>
      )}
    </div>
  );
}

export default MoodSelector;
